import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  editResourceTemplate,
  getAllResourceTemplates,
  getListOfBuildings,
  saveResourceTemplate,
} from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import { BuildingsModel, ResourceTemplateModel } from '../../../../types';
import { SubmitHandler, useForm } from 'react-hook-form';

import ChangeLogDialog from '../../../shared/change-log-dialog';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  CalendarRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { Messages } from '../../../utils/message-constants';
import SimpleFormPage from '../../../shared/simple-form-page';
import UnsavedChangesDialog, {
  useUnsavedChanges,
} from '../../../shared/unsaved-changes-dialog';

import { pagesConfig } from '../../../shared/pages-config';
import { useNavigate, useParams } from 'react-router-dom';
import { useUserSettings } from '../../../utils/use-user-settings';
import StepBasicInformation from './form-step/step-basic-information';
import StepTemplateShares from './form-step/step-template-shares';
import StepReview from './form-step/step-review';
import FormSkeleton from './form-step/form-skeleton';
import SharedFormStepper, {
  StepConfig,
} from '../../../shared/shared-form-stepper';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import SimpleBreadcrumbsPage from '../../../shared/simple-breadcrumbs-page';
import Box from '@mui/material/Box';
export interface FormValue extends ResourceTemplateModel {
  building?: BuildingsModel | null;
}

const CoordinatesSchema = z.object({
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
});

const BuildingAddressSchema = z.object({
  regionCode: z.string(),
  languageCode: z.string().nullable(),
  postalCode: z.string().nullable(),
  administrativeArea: z.string().nullable(),
  locality: z.string().nullable(),
  sublocality: z.string().nullable(),
  addressLines: z.array(z.string()).nullable(),
});

const BuildingSchema = z.object({
  etags: z.string(),
  buildingId: z.string(),
  buildingName: z.string(),
  description: z.string(),
  coordinates: CoordinatesSchema.nullable(),
  floorNames: z.array(z.string()),
  address: BuildingAddressSchema.nullable(),
});

const BasicInformationSchema = z.object({
  name: z.string().min(1, 'Template Name is required.'),
  desc: z.string().min(1, 'Template Description is required.'),
  resourceCategory: z.string(),
  resourceType: z.string(),
  buildingId: z.string(),
  building: BuildingSchema.nullable(),
  floorName: z.string(),
  floorSection: z.string(),
  capacity: z.number().nullable(),
});

const TemplateSharesSchema = z.object({
  calSharedToDomain: z.boolean(),
  domainRole: z.enum(['freeBusyReader', 'reader']),
  calPublic: z.boolean(),

  publicRole: z.enum(['freeBusyReader', 'reader']),
  shares: z.object({}).catchall(z.any()),
});

const ResourceTemplateSchema = z
  .object({
    id: z.number(),
    dateCreated: z.number(),
    dateUpdated: z.number(),
    createdBy: z.string().nullable(),
    updatedBy: z.string().nullable(),
  })
  .and(BasicInformationSchema)
  .and(TemplateSharesSchema);

export type ResourceTemplateFormValues = z.infer<typeof ResourceTemplateSchema>;

export default function ResourceTemplatesFormPage() {
  const { changeLogRoles } = useUserSettings();
  const { createEvent } = useAnalytics();
  const { id } = useParams();
  const selectedRowId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [activeStep, setActiveStep] = useState(0);

  const steps: StepConfig[] = [
    {
      label: 'Basic Information',
      schema: BasicInformationSchema,
    },
    {
      label: 'Template Shares',
      schema: TemplateSharesSchema,
    },
    {
      label: 'Review',
    },
  ];
  const {
    data: resourceTemplateModels,
    isLoading: resourceTemplateLoading,
    isFetching: resourceTemplateFetching,
  } = useQuery({
    queryKey: [UserKeys.CalendarResourceTemplates],
    queryFn: () => getAllResourceTemplates(),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('resource templates'),
    },
  });

  const {
    data: buildingsList,
    isLoading: isBuildingLoading,
    isFetching: isBuildingFetching,
  } = useQuery({
    queryKey: [UserKeys.BuildingsList],
    queryFn: () => getListOfBuildings(),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('building'),
    },
  });

  const selectedResourceTemplate = resourceTemplateModels?.find(
    (resourceTemplate) => resourceTemplate.id === selectedRowId
  );

  const isLoading =
    resourceTemplateLoading ||
    resourceTemplateFetching ||
    isBuildingLoading ||
    isBuildingFetching;

  const [stepCompleted, setStepCompleted] = useState(() =>
    steps.map(() => (selectedRowId ? true : false))
  );

  const baseDefaults: ResourceTemplateFormValues = {
    id: 0,
    name: '',
    desc: '',
    createdBy: '',
    updatedBy: '',
    dateCreated: new Date().getTime(),
    dateUpdated: 0,
    shares: {},
    buildingId: '',
    floorName: '',
    resourceType: '',
    capacity: 1,
    floorSection: '',
    resourceCategory: 'Resource Category',
    calSharedToDomain: true,
    calPublic: false,
    domainRole: 'freeBusyReader',
    publicRole: 'reader',
    building: null,
  };

  const formConfig =
    selectedResourceTemplate && selectedRowId
      ? {
          resolver: zodResolver(ResourceTemplateSchema),
          values: {
            ...baseDefaults,
            ...selectedResourceTemplate,
            building:
              buildingsList?.find(
                (b) => b.buildingId === selectedResourceTemplate.buildingId
              ) ?? null,
            createdBy: selectedResourceTemplate.createdBy ?? '',
            updatedBy: selectedResourceTemplate.updatedBy ?? '',
            dateUpdated:
              selectedResourceTemplate.dateUpdated ?? new Date().getTime(),
          },
        }
      : {
          resolver: zodResolver(ResourceTemplateSchema),
          defaultValues: baseDefaults,
        };

  const {
    control,
    handleSubmit,
    formState: { errors, isValidating, isSubmitting, isDirty },
    trigger,
    reset,
    getValues,
    setValue,
    watch,
  } = useForm<ResourceTemplateFormValues>(formConfig);

  const handleCancel = () => {
    navigate(`/${pagesConfig.resourceTemplates.link}`, {
      state: { fromCancelButton: true },
    });
  };

  const { mutate, isPending } = useMutation({
    mutationFn: (formData: FormValue) => {
      const cleanData = { ...formData };

      if (selectedRowId) {
        return editResourceTemplate(cleanData, changeLogMessage);
      } else {
        delete cleanData.building;
        return saveResourceTemplate(cleanData, changeLogMessage);
      }
    },
    onSuccess: (returnedTemplate) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: selectedRowId
          ? Messages.Success.Updated('Resource template')
          : Messages.Success.Created('Resource template'),
      });

      queryClient.setQueryData(
        [UserKeys.CalendarResourceTemplates],
        selectedResourceTemplate
          ? (oldData: ResourceTemplateModel[]) => {
              const newList = oldData.filter(
                (model) => model.id !== selectedResourceTemplate.id
              );
              return [...newList, returnedTemplate];
            }
          : (oldData: ResourceTemplateModel[]) => {
              return [...oldData, returnedTemplate];
            }
      );
      setOpenChangeLogDialog(false);
      setChangeLogMessage('');
      setActiveStep(0);
      reset();
      bypass();
      navigate(`/${pagesConfig.resourceTemplates.link}`);
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: selectedRowId
          ? Messages.Errors.Network.Updating('this resource template')
          : Messages.Errors.Network.Creating('this resource template'),
      });
    },
    onSettled: () => {
      createEvent(
        selectedRowId
          ? analytics.calendars.resource_templates.update_resource_template
          : analytics.calendars.resource_templates.create_resource_template,
        {
          module: ModuleList.modules.calendars.name,
          section: ModuleList.modules.calendars.sections.resourceTemplates,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });
  const onSubmit: SubmitHandler<ResourceTemplateFormValues> = (data) => {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        selectedRowId
          ? CalendarRoles.EditResourceTemplate.value
          : CalendarRoles.CreateResourceTemplate.value
      ) ||
      (isAbleToSeeUIElement(
        changeLogRoles,
        CalendarRoles.CanSelectCategoryUnknown.value
      ) &&
        data.resourceCategory === 'CATEGORY_UNKNOWN') ||
      (isAbleToSeeUIElement(
        changeLogRoles,
        CalendarRoles.CanSelectConferenceRoom.value
      ) &&
        data.resourceCategory === 'CONFERENCE_ROOM') ||
      (isAbleToSeeUIElement(
        changeLogRoles,
        CalendarRoles.CanSelectOther.value
      ) &&
        data.resourceCategory === 'OTHER')
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate(data);
    }
  };

  const formStepperProps = {
    activeStep,
    setActiveStep,
    steps,
    errors,
    trigger,
    stepCompleted,
    setStepCompleted,
  };

  const data = getValues() as FormValue;

  const {
    showDialog: showUnsavedChangesDialog,
    handleConfirm: handleUnsavedConfirm,
    handleCancel: handleUnsavedCancel,
    bypass,
    isCancelAction,
  } = useUnsavedChanges(isDirty);

  return (
    <SimpleBreadcrumbsPage
      isSubPage
      breadcrumbs={[
        { label: ModuleList.modules.calendars.name },
        {
          label: pagesConfig.resourceTemplates.name,
          path: pagesConfig.resourceTemplates.link,
        },
      ]}
    >
      <SimpleFormPage
        title={
          !selectedRowId
            ? pagesConfig.createResourceTemplate.name
            : pagesConfig.editResourceTemplate.name
        }
        stepper={
          <SharedFormStepper<ResourceTemplateFormValues>
            {...formStepperProps}
            errors={errors}
          />
        }
        {...formStepperProps}
        content={
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {isLoading && <FormSkeleton />}
            {!isLoading && steps[activeStep].label === 'Basic Information' && (
              <StepBasicInformation
                control={control}
                errors={errors}
                watch={watch}
                setValue={setValue}
                buildingsList={buildingsList || []}
                isLoading={isBuildingLoading}
                isFetching={isBuildingFetching}
              />
            )}
            {!isLoading && steps[activeStep].label === 'Template Shares' && (
              <StepTemplateShares
                control={control}
                watch={watch}
                setValue={setValue}
              />
            )}
            {!isLoading && steps[activeStep].label === 'Review' && (
              <StepReview selectedTemplate={data} />
            )}
          </Box>
        }
        handleDiscard={handleCancel}
        handleSubmit={handleSubmit(onSubmit)}
        submitButtonText={
          !selectedResourceTemplate
            ? pagesConfig.createResourceTemplate.name
            : 'Update Resource Template'
        }
        isLoadingSubmit={isValidating || isSubmitting || isPending}
        isDisableSubmit={isLoading}
      />

      <UnsavedChangesDialog
        isCancelAction={isCancelAction}
        entityName="Resource Template"
        pageMode={selectedRowId ? 'edit' : 'create'}
        open={showUnsavedChangesDialog}
        handleConfirm={handleUnsavedConfirm}
        handleClose={handleUnsavedCancel}
      />
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => {
          const newTemplate: ResourceTemplateFormValues = {
            ...getValues(),
          };
          mutate(newTemplate);
        }}
        mutateLoading={isPending}
      />
    </SimpleBreadcrumbsPage>
  );
}
